"use client";

import { useRef, useMemo } from "react";
import Link from "next/link";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

function FloatingGeometry() {
  const groupRef = useRef<THREE.Group>(null);
  const mesh1Ref = useRef<THREE.Mesh>(null);
  const mesh2Ref = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (groupRef.current) {
      groupRef.current.rotation.y = t * 0.02;
    }
    if (mesh1Ref.current) {
      mesh1Ref.current.rotation.x = t * 0.1;
      mesh1Ref.current.rotation.z = t * 0.05;
      mesh1Ref.current.position.y = Math.sin(t * 0.3) * 0.3;
    }
    if (mesh2Ref.current) {
      mesh2Ref.current.rotation.y = t * 0.08;
      mesh2Ref.current.position.y = Math.sin(t * 0.4 + 1) * 0.2;
    }
  });

  return (
    <group ref={groupRef}>
      <mesh ref={mesh1Ref} position={[2.5, 0.5, -1]}>
        <octahedronGeometry args={[2, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.3} transparent />
      </mesh>
      <mesh ref={mesh2Ref} position={[-2, -0.5, -2]}>
        <icosahedronGeometry args={[1.5, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.2} transparent />
      </mesh>
    </group>
  );
}

function DotGrid() {
  const pointsRef = useRef<THREE.Points>(null);

  const positions = useMemo(() => {
    const pos = [];
    const gridSize = 20;
    const spacing = 0.8;
    for (let x = -gridSize / 2; x < gridSize / 2; x++) {
      for (let y = -gridSize / 2; y < gridSize / 2; y++) {
        pos.push(x * spacing, y * spacing, -5);
      }
    }
    return new Float32Array(pos);
  }, []);

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.z = state.clock.elapsedTime * 0.01;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
        />
      </bufferGeometry>
      <pointsMaterial color="#1a1a1a" size={0.015} opacity={0.08} transparent />
    </points>
  );
}

function Scene() {
  return (
    <>
      <FloatingGeometry />
      <DotGrid />
    </>
  );
}

export default function NotFound() {
  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col relative overflow-hidden">
      {/* Three.js Canvas Background */}
      <div className="fixed inset-0 z-0" aria-hidden="true">
        <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
          <Scene />
        </Canvas>
      </div>

      <div className="relative z-10 flex flex-col min-h-screen">
        <Navigation />

        <main id="main-content" className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-xl">
            <p className="text-[#ff6900] font-medium mb-4">404</p>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">
              Page not found
            </h1>
            <p className="text-lg text-[#1a1a1a]/70 mb-8">
              The page you&apos;re looking for doesn&apos;t exist or has been moved.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/"
                className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-gradient-to-r from-[#ff6900] to-[#c2410c] text-white font-medium hover:opacity-90 transition-opacity"
              >
                Back to home
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-3 rounded-full border border-[#1a1a1a]/20 text-[#1a1a1a] font-medium hover:border-[#ff6900]/40 transition-colors"
              >
                Contact us
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
}
